import TrashIcon from '@heroicons/react/outline/esm/TrashIcon.js'
import { clsx } from 'clsx'

import { Post, PostStatus } from '@tribeplatform/gql-client/types'
import { useRouter } from '@tribeplatform/react-sdk'
import { Avatar } from '@tribeplatform/react-ui-kit/Avatar'

import { Time } from '../common/components/Time.js'
import { dayjs } from '../common/lib/dayjs.js'
import { T } from '../i18n/components/T.js'
import { MemberBadgeLocation } from '../MemberBadge/MemberBadge.js'
import { MemberBadges } from '../MemberBadge/MemberBadges.js'

export const PostMemberBar = ({
  post,
  showSpace = true,
}: {
  showSpace?: boolean
  post: Partial<Post>
}) => {
  const { Link } = useRouter()
  const member = post?.owner?.member

  const postDate = post?.publishedAt || post?.createdAt

  return (
    <div
      className={clsx(
        'flex flex-1 space-s-4 items-center overflow-hidden post-member-bar',
        !member && 'animate-pulse',
      )}
    >
      {member ? (
        <Link
          href={member?.relativeUrl}
          className="flex-shrink-0"
          variant="inherit"
          tabIndex={-1}
        >
          <Avatar
            name={member?.name}
            size="lg"
            src={
              member?.profilePicture && 'urls' in member?.profilePicture
                ? member?.profilePicture?.urls?.thumb
                : undefined
            }
          />
        </Link>
      ) : (
        <div className="pt-1">
          <Avatar
            name={member?.name}
            size="lg"
            src={
              member?.profilePicture && 'urls' in member?.profilePicture
                ? member?.profilePicture?.urls?.thumb
                : undefined
            }
          />
        </div>
      )}
      <div className="overflow-hidden flex-grow pt-1">
        <div className="truncate max-w-full flex items-center text-content">
          <div className="flex-1 flex items-center overflow-hidden flex-wrap">
            <div className="truncate me-2" title={member?.name}>
              <Link
                href={member?.relativeUrl}
                variant="inherit"
                className="font-medium truncate"
                translate="no"
              >
                {member?.name ? (
                  <>{member?.name}</>
                ) : (
                  <div className="h-4 w-32 bg-surface-pressed rounded-full"></div>
                )}
              </Link>
            </div>
            <MemberBadges
              member={member}
              size="xs"
              location={MemberBadgeLocation.PostMemberBar}
            />
          </div>
        </div>
        <div className="text-sm text-content-subdued truncate mt-1">
          {post?.status === PostStatus.DELETED && (
            <span>
              <span className="text-action-destructive inline-flex items-center">
                <span>
                  <T defaultMessage="Deleted" id="Generics.Deleted" />
                </span>
                <TrashIcon className="w-4 h-4 ms-0.5" />
              </span>
              {/* eslint-disable-next-line react/jsx-no-literals */}
              <span>&nbsp;·&nbsp;</span>
            </span>
          )}
          {showSpace && post.space?.slug ? (
            <span>
              <Link variant="inherit" href={post.relativeUrl}>
                <Time dateTime={postDate}>{dayjs(postDate).fromNow()}</Time>
              </Link>
              {/* eslint-disable-next-line react/jsx-no-literals */}
              <span>&nbsp;·&nbsp;</span>
              <span title={post.space.name}>
                <T
                  defaultMessage="Posted in {space}"
                  id="PostMemberBar.PostSpace"
                  values={{
                    space: (
                      <span>
                        <Link variant="inherit" href={post.space.relativeUrl}>
                          {post.space.name}
                        </Link>
                      </span>
                    ),
                  }}
                />
              </span>
            </span>
          ) : (
            <>
              <Link variant="inherit" href={post.relativeUrl}>
                <Time dateTime={postDate}>
                  {member?.name ? (
                    <>{dayjs(postDate).fromNow()}</>
                  ) : (
                    <div className="h-3 w-16 bg-surface-pressed rounded-full"></div>
                  )}
                </Time>
              </Link>
              {member?.tagline ? (
                // eslint-disable-next-line react/jsx-no-literals
                <span>&nbsp;·&nbsp;{member?.tagline}</span>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
